import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Check, Linkedin } from "lucide-react";
import { toast } from "sonner";

// Tiny inline X (Twitter) logo — lucide's Twitter icon is deprecated.
function XIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

/**
 * SocialShareButtons
 * @param {string} url        The share URL to spread
 * @param {string} text       Short tweet/post text (the URL is appended automatically by each platform)
 * @param {string} [size]     Button size — defaults to "sm"
 * @param {string} [variant]  "default" (filled, for dialogs) or "subtle" (chip row, for the public report)
 * @param {function} [onShare] Optional callback fired on every click (e.g., analytics)
 */
export function SocialShareButtons({
  url,
  text,
  size = "sm",
  variant = "default",
  onShare,
}) {
  const [copied, setCopied] = useState(false);

  if (!url) return null;

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text || "");

  const xHref = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
  const liHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

  const openShareWindow = (href, network) => {
    onShare?.(network);
    window.open(
      href,
      "_blank",
      "noopener,noreferrer,width=600,height=540"
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      onShare?.("copy");
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select the text manually.");
    }
  };

  if (variant === "subtle") {
    // Chip-style row used on the public /share/:token page
    return (
      <div
        className="flex items-center gap-2 flex-wrap"
        data-testid="social-share-buttons"
      >
        <button
          type="button"
          onClick={() => openShareWindow(xHref, "x")}
          className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 hover:border-black/30 hover:bg-[#F7F5F0] text-[#1E392A] text-sm px-3.5 py-1.5 transition-colors"
          aria-label="Share on X"
          data-testid="share-x-btn"
        >
          <XIcon className="w-3.5 h-3.5" />
          <span>Post</span>
        </button>
        <button
          type="button"
          onClick={() => openShareWindow(liHref, "linkedin")}
          className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 hover:border-black/30 hover:bg-[#F7F5F0] text-[#1E392A] text-sm px-3.5 py-1.5 transition-colors"
          aria-label="Share on LinkedIn"
          data-testid="share-linkedin-btn"
        >
          <Linkedin className="w-3.5 h-3.5" />
          <span>LinkedIn</span>
        </button>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 hover:border-black/30 hover:bg-[#F7F5F0] text-[#1E392A] text-sm px-3.5 py-1.5 transition-colors"
          aria-label="Copy link"
          data-testid="share-copy-btn"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? "Copied" : "Copy link"}</span>
        </button>
      </div>
    );
  }

  // Default — used inside the owner's share dialog
  return (
    <div
      className="flex items-center gap-2 flex-wrap"
      data-testid="social-share-buttons"
    >
      <Button
        type="button"
        size={size}
        variant="outline"
        onClick={() => openShareWindow(xHref, "x")}
        className="rounded-full"
        data-testid="share-x-btn"
      >
        <XIcon className="w-4 h-4 mr-1.5" /> Post on X
      </Button>
      <Button
        type="button"
        size={size}
        variant="outline"
        onClick={() => openShareWindow(liHref, "linkedin")}
        className="rounded-full"
        data-testid="share-linkedin-btn"
      >
        <Linkedin className="w-4 h-4 mr-1.5" /> LinkedIn
      </Button>
      <Button
        type="button"
        size={size}
        variant="outline"
        onClick={handleCopy}
        className="rounded-full"
        data-testid="share-copy-btn"
      >
        {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
        {copied ? "Copied" : "Copy link"}
      </Button>
    </div>
  );
}

/**
 * Build a short, punchy share-text from an analysis object.
 * Prefers the AI headline; falls back to a generic line.
 */
export function buildAnalysisShareText(analysis, { ownerView = false } = {}) {
  const headline =
    analysis?.ai_summary?.headline ||
    analysis?.headline ||
    null;

  if (headline) {
    const cleaned = String(headline).replace(/\s+/g, " ").trim();
    // Cap to ~200 chars so the URL + handles fit a tweet comfortably.
    const trimmed = cleaned.length > 200 ? cleaned.slice(0, 197) + "…" : cleaned;
    return ownerView
      ? `${trimmed} — translated from my Google Analytics by Plainly:`
      : `${trimmed} — read the full plain-English breakdown by Plainly:`;
  }
  return ownerView
    ? "I just got my Google Analytics translated into plain English by Plainly — see what it caught:"
    : "Someone shared a plain-English Google Analytics read-out with me — built by Plainly:";
}
